import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';

import { useSetRecoilState } from 'recoil';
import { dialogState, ToastSeverity, toastStateSelector } from 'state';

import { GeneralIds, ModelTypes, RoutePaths } from 'types/types';
import { useNavigate, useParams } from 'react-router-dom';
import { getRoutePath } from 'utils';
import * as yup from 'yup';
import { mutation } from 'operations';
import { FormTemplateEditorMode } from 'routes/FormTemplateEditor/FormTemplateEditor';
import { FormTemplateType } from 'api/models';

export function AddReportTemplateDialog() {
  const closeDialog = useSetRecoilState(dialogState);
  const setToast = useSetRecoilState(toastStateSelector);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { projectId } = useParams<GeneralIds>();

  const formik = useFormik({
    initialValues: {
      name: '',
    },
    validationSchema: yup.object({
      name: yup.string().required(t('general.errors.required') ?? ''),
    }),

    onSubmit: async (values, { resetForm }) => {
      const { name } = values;
      try {
        const formTemplateType = await mutation<FormTemplateType>({
          type: ModelTypes.formTemplateType,
          body: {
            id: '',
            name,
            type: FormTemplateEditorMode.report,
          },
          ids: {
            projectId,
          },
        });

        resetForm();
        closeDialog({ isOpen: false });

        const formTemplateTypeId = formTemplateType?.data?.id;

        if (!formTemplateTypeId) {
          setToast({
            isOpen: true,
            message: t('general.errors.save'),
            severity: ToastSeverity.error,
          });
          return;
        }

        const path = getRoutePath(RoutePaths.reportTemplateEditor, {
          formTemplateTypeId,
          projectId,
        });

        navigate(path);
      } catch (e: any) {
        const errorCode = e.response?.data?.error?.[0].code;
        setToast({
          isOpen: true,
          message: i18n.exists(`errors.${errorCode}`)
            ? t(`errors.${errorCode}`)
            : t('general.errors.save'),
          severity: ToastSeverity.error,
        });
      }
    },
  });

  return (
    <Box>
      <DialogTitle>{t('dialogs.addReportTemplate.title')}</DialogTitle>
      <DialogContent>
        <form>
          <TextField
            sx={{ mt: 1 }}
            fullWidth
            label={t('general.name') + ' *'}
            type="name"
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </form>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => closeDialog({ isOpen: false })}
          color="secondary"
          variant="contained"
          sx={{ mb: 1 }}
        >
          {t('general.cancel')}
        </Button>
        <Button
          type="submit"
          color="primary"
          variant="contained"
          onClick={formik.submitForm}
          sx={{ mr: 2, mb: 1 }}
        >
          {t('general.create')}
        </Button>
      </DialogActions>
    </Box>
  );
}
